import * as signalR from '@microsoft/signalr';

// The wire contract of webapi's VoiceHub (#2578): webapi/Models/Voice/VoiceProtocol.cs.

export interface VoiceClientFrame {
    t: 'audio' | 'auth.refresh';
    pcm?: string;
    token?: string;
}

export interface VoiceServerEvent {
    t: string;
    maxSeconds?: number;
    authExpiresAt?: string;
    expiresAt?: string;
    responseId?: string;
    turnId?: string;
    pcm?: string;
    interrupted?: boolean;
    reason?: string;
    name?: string;
    /** On `session.ready`: sent back unchanged when the conversation reconnects, never compared here. */
    conversationStartedAt?: string;
}

export interface VoiceEnd {
    reason: string;
}

/** Everything about the hub connection the client needs, so tests can run without SignalR. */
export interface VoiceHubConnection {
    start(): Promise<void>;
    stop(): Promise<void>;
    onEvent(handler: (event: VoiceServerEvent) => void): void;
    converse(
        chatId: string,
        hostContext: string,
        frames: signalR.Subject<VoiceClientFrame>,
        conversationStartedAt?: string,
    ): Promise<VoiceEnd>;
}

export interface VoiceSessionCallbacks {
    onEvent(event: VoiceServerEvent): void;
    onEnded(reason: string): void;
    /** A fresh access token for `auth.refresh`; undefined when none is available. */
    getFreshToken(): Promise<string | undefined>;
}

/** How long a stopped session waits for the server's `ended` before closing the connection itself. */
export const STOP_GRACE_MS = 5000;

// A refused start comes back as a HubException with the server's error code; SignalR puts "HubException: <message>"
// in the error it sends. A user who may not use voice at all (flag off, external) is refused earlier, by
// VoiceHub.OnConnectedAsync aborting the connection, which reaches us only as a close before any event. On a
// reconnect such a close is more likely the app going down again mid-deploy, so it ends as `connect_failed`, which the
// reconnect policy retries within its caps.
const failedConverseReason = (message: string, heardFromServer: boolean, isReconnect: boolean): string => {
    if (message.includes('voice_unavailable')) {
        return 'voice_unavailable';
    }
    if (heardFromServer) {
        return 'disconnected';
    }
    return isReconnect && !message.includes('HubException') ? 'connect_failed' : 'voice_unavailable';
};

/**
 * One voice session over `/voiceHub`: one streaming `ConverseAsync` invocation for its whole life.
 * Audio is only forwarded once the server says `session.ready` — before that the provider session
 * has no confirmed transcription and the server drops (and counts) early frames.
 */
export class VoiceSessionClient {
    private readonly frames = new signalR.Subject<VoiceClientFrame>();
    private ready = false;
    private closed = false;
    private ended = false;
    private heardFromServer = false;
    private stopTimer: ReturnType<typeof setTimeout> | undefined;

    constructor(
        private readonly connection: VoiceHubConnection,
        private readonly callbacks: VoiceSessionCallbacks,
    ) {}

    get isReady(): boolean {
        return this.ready && !this.closed;
    }

    /**
     * @param conversationStartedAt Only for a reconnect: the dropped conversation's `session.ready` value. The server
     * then refuses the start (`reconnect_superseded`) if the user has started a newer conversation since.
     */
    async start(chatId: string, hostContext: string, conversationStartedAt?: string): Promise<void> {
        this.connection.onEvent((event) => {
            this.handle(event);
        });
        await this.connection.start();
        if (this.closed) {
            // Stopped while the hub was connecting. SignalR's Subject does not replay a completion to a
            // later subscriber, so invoking now would open a server session whose upload never ends.
            this.finish('stopped');
            return;
        }
        this.connection
            .converse(chatId, hostContext, this.frames, conversationStartedAt)
            .then((end) => {
                this.finish(end.reason);
            })
            .catch((error: unknown) => {
                const message = error instanceof Error ? error.message : String(error);
                this.finish(failedConverseReason(message, this.heardFromServer, conversationStartedAt !== undefined));
            });
    }

    /** Sends one 10–100 ms PCM16 frame (base64); silently dropped until the session is ready. */
    sendAudio(pcmBase64: string): boolean {
        if (!this.isReady) {
            return false;
        }
        this.frames.next({ t: 'audio', pcm: pcmBase64 });
        return true;
    }

    /** Ends the session the way the server expects: completing the upload stream. */
    stop(): void {
        if (this.closed) {
            return;
        }
        this.closed = true;
        this.frames.complete();
        // The server sends `ended` only after its drain; a stalled network would hold the connection until
        // SignalR's serverTimeout. The server finishes its writes either way once it has seen the completion.
        this.stopTimer = setTimeout(() => {
            this.finish('stopped');
        }, STOP_GRACE_MS);
    }

    private handle(event: VoiceServerEvent): void {
        this.heardFromServer = true;
        if (event.t === 'ended') {
            this.finish(event.reason ?? 'ended');
            return;
        }
        if (this.closed) {
            // Stopped: the caller has torn its audio down, so the answer still streaming in goes nowhere.
            return;
        }
        if (event.t === 'session.ready') {
            this.ready = true;
        } else if (event.t === 'auth.required') {
            void this.refresh();
        }
        this.callbacks.onEvent(event);
    }

    // Answers every ask, even with a token already sent: the server asks again after a refresh that failed
    // for reasons of its own (deny list, IdP), and it spaces the validations itself.
    private async refresh(): Promise<void> {
        const token = await this.callbacks.getFreshToken();
        if (token && !this.closed) {
            this.frames.next({ t: 'auth.refresh', token });
        }
    }

    private finish(reason: string): void {
        this.ready = false;
        clearTimeout(this.stopTimer);
        if (!this.closed) {
            this.closed = true;
            this.frames.complete();
        }
        if (this.ended) {
            return;
        }
        this.ended = true;
        this.callbacks.onEnded(reason);
        void this.connection.stop();
    }
}

/** The real connection: same transport and token handling as the message relay hub. */
export const createVoiceHubConnection = (
    backendUrl: string,
    providerName: string,
    getToken: () => string | undefined,
): VoiceHubConnection => {
    const url = new URL('/voiceHub', backendUrl);
    // The provider rides in the query: a browser WebSocket cannot set headers (OidcProviderSelector).
    url.searchParams.append('oidcprovider', providerName);
    const hub = new signalR.HubConnectionBuilder()
        .withUrl(url.toString(), {
            skipNegotiation: true,
            transport: signalR.HttpTransportType.WebSockets,
            logger: signalR.LogLevel.Warning,
            accessTokenFactory: () => getToken() ?? '',
        })
        .withHubProtocol(new signalR.JsonHubProtocol())
        .build();

    return {
        start: () => hub.start(),
        stop: () => hub.stop(),
        onEvent: (handler) => {
            hub.on('VoiceEvent', handler);
        },
        // JSON leaves out an undefined conversationStartedAt, which the server takes for a new conversation.
        converse: (chatId, hostContext, frames, conversationStartedAt) =>
            hub.invoke<VoiceEnd>('ConverseAsync', chatId, { v: 1, hostContext, conversationStartedAt }, frames),
    };
};
